import {
  DynamoDbLocationDetail,
  DynamoDbFODwellingRisk,
  DynamoDbFOPropertyStructuresRisk,
  DynamoDbFarmRiskDetail
} from '@ourbranch/schema-types/dynamodb';
import { FarmAddressDetails } from '@ourbranch/schema-types/staff-appsync';
import { FarmRiskType, FarmScheduleCoverageType } from '@ourbranch/lookups';
import { policyTypes, isFarmBasedPolicyType } from '@ourbranch/policy-types';

export const DWELLING_RISK_TYPES = [FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3];

// #35954: the mobile/modular propertyType codes the CFM Coverage A inflation guard excludes (mirrors
// the brand config's excludedPropertyTypes for FO2/FO3). A dwelling qualifies for the 5% guard when it
// is RCV (RC loss settlement) and its building.propertyType is NOT one of these. #36674 P5b removed the
// guard's 2026-12-16 start date: it applies to every renewal EmberTech generates.
export const CFM_INFLATION_GUARD_EXCLUDED_PROPERTY_TYPES = ['MH', 'MD'];

export { isGoInflationGuardEligibleRisk } from './go-inflation-guard';

export {
  FUNCTIONAL_REPLACEMENT_COST_YEAR_THRESHOLD,
  isFunctionalReplacementCostApplied
} from './functional-replacement-cost';

// FARM_BASED_POLICY_TYPES / isFarmBasedPolicyType now live in the zero-dependency
// @ourbranch/policy-types package so every layer (FE, low-level packages) can import the
// canonical farm check without pulling in farm-utils' heavier schema-types dependency.
// Re-exported here for backwards compatibility with existing farm-utils importers.
export { FARM_BASED_POLICY_TYPES, isFarmBasedPolicyType } from '@ourbranch/policy-types';

// #33168: "Liability" on the staff-fe new-offer Risk Type dropdown is a form-only sentinel, NOT a
// FarmRiskType — no risk record is ever created from it. Selecting it produces a policyType F
// quote whose location is submitted with no risks and includeFarmLiability on; the submit mapper
// strips any sentinel-typed risk before it reaches the wire. Deliberately not FO-shaped so it can
// never be mistaken for (or collide with) a real risk type.
export const FARM_LIABILITY_RISK_SELECTION = 'LiabilityOnly';

export const isLiabilityOnlyRiskSelection = (riskType: string | null | undefined): boolean =>
  riskType === FARM_LIABILITY_RISK_SELECTION;

// #32514: a Homeowners (Z) or Rental (Y) farm-based policy must insure a dwelling — you cannot
// insure only an FO-6 (other structures). Home accepts FO-1/2/3/4; Rental accepts FO-1/2/3 (Rental
// has no FO-4). Full Farm (F) and non-farm products have no dwelling requirement (a farm may insure
// only structures/equipment). Shared by the staff-fe validation schema and both carriers' backend
// underwriting rules so the eligibility check is identical on the FE agent flow and the backend.
// Which products actually enforce it is a per-carrier decision made at the call site, not here:
// Forreston applies it to both Home and Rental; CFM applies it to Rental only (#34532) and leaves
// Home on `hasInsuredFarmProperty` below (an FO-6 alone is eligible on CFM Home, #34260, from the
// #32753 parity port). Do not widen CFM Home to require a dwelling without product sign-off.
export const HOME_DWELLING_RISK_TYPES: readonly string[] = [
  FarmRiskType.Fo1,
  FarmRiskType.Fo2,
  FarmRiskType.Fo3,
  FarmRiskType.Fo4
];
export const RENTAL_DWELLING_RISK_TYPES: readonly string[] = DWELLING_RISK_TYPES;

const REQUIRED_DWELLING_RISK_TYPES_BY_POLICY_TYPE: Record<string, readonly string[]> = {
  [policyTypes.FarmHome]: HOME_DWELLING_RISK_TYPES,
  [policyTypes.FarmRental]: RENTAL_DWELLING_RISK_TYPES
};

/**
 * Returns the dwelling risk types that satisfy a farm product's "must insure a dwelling" rule, or
 * null if the product has no such rule (full Farm, non-farm). (#32514)
 */
export function getRequiredDwellingRiskTypes(policyType: string | null | undefined): readonly string[] | null {
  if (!policyType) {
    return null;
  }
  return REQUIRED_DWELLING_RISK_TYPES_BY_POLICY_TYPE[policyType] ?? null;
}

/** Whether the given farm product must insure a dwelling (Home/Rental) at all. (#32514) */
export function farmPolicyRequiresDwelling(policyType: string | null | undefined): boolean {
  return getRequiredDwellingRiskTypes(policyType) !== null;
}

// Minimal structural shape needed to evaluate the dwelling requirement. Kept permissive so both the
// backend (DynamoDbLocationDetail[]) and the staff-fe form values (looser objects) can be passed
// without casts. (#32514)
interface DwellingRequirementLocation {
  isDeleted?: boolean | null;
  risks?: ({ type?: string | null; isDeleted?: boolean | null } | null | undefined)[] | null;
}

/**
 * Whether a farm product's dwelling requirement is met — i.e. at least one non-deleted location
 * carries a qualifying dwelling risk. Products without a dwelling requirement (full Farm, non-farm)
 * always return true. (#32514)
 */
export function hasRequiredDwelling(
  policyType: string | null | undefined,
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined
): boolean {
  const required = getRequiredDwellingRiskTypes(policyType);
  if (!required) {
    return true;
  }
  const requiredUpper = required.map((type) => type.toUpperCase());
  return (farmLocations || [])
    .filter((location): location is DwellingRequirementLocation => !!location && !location.isDeleted)
    .some((location) =>
      (location.risks || []).some((risk) => !!risk?.type && requiredUpper.includes(String(risk.type).toUpperCase()))
    );
}

/**
 * Whether the policy insures any property — one non-deleted dwelling, renters or property risk. CFM
 * Home (Z) / Rental (Y) require this in place of the #32514 dwelling rule (#34260); a risk-less
 * policy still fails it because liability-only is Farm-only (#33169). Tombstones do not count, as in
 * `collectQuotableFarmRiskTypes`.
 */
export function hasInsuredFarmProperty(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  allowedRiskTypes?: readonly string[] | null
): boolean {
  return collectQuotableFarmRiskTypes(farmLocations, allowedRiskTypes).some(isInsuredFarmPropertyRiskType);
}

/**
 * The risk types across the given locations that the brand can actually quote. Deleted locations are
 * skipped, and so are deleted risks: a segment delta sends removals as tombstones with a back-filled
 * `type`, so counting them would read "remove the dwelling and add inland marine in one endorsement" as
 * a mix. Risk types outside `allowedRiskTypes` are dropped downstream by the brand gate and can never
 * reach the rater, so they don't count either. (#33165)
 */
export function collectQuotableFarmRiskTypes(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  allowedRiskTypes?: readonly string[] | null
): string[] {
  const allowedUpper = allowedRiskTypes?.map((riskType) => riskType.toUpperCase());
  return (farmLocations || [])
    .filter((location): location is DwellingRequirementLocation => !!location && !location.isDeleted)
    .flatMap((location) => (location.risks || []).filter((risk) => !risk?.isDeleted).map((risk) => risk?.type))
    .filter((riskType): riskType is string => !!riskType)
    .filter((riskType) => !allowedUpper || allowedUpper.includes(riskType.toUpperCase()));
}

/**
 * Whether the policy is liability-only — farm liability with nothing insured under it. The flag is
 * compared to `true` because an offer that never set it is not carrying liability, and the risk set
 * comes from `collectQuotableFarmRiskTypes` so an endorsement's tombstoned removals are not counted
 * as surviving risks.
 */
export function isLiabilityOnlyFarmPolicy(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  includeFarmLiability: boolean | null | undefined
): boolean {
  return includeFarmLiability === true && collectQuotableFarmRiskTypes(farmLocations).length === 0;
}

/**
 * The liability types a policy type mask allows, narrowed by its liability-only list when the policy
 * insures nothing but liability. An empty narrowed list falls back to the general one rather than
 * filtering every option away. Returns undefined when the mask restricts nothing.
 */
export function getAllowedLiabilityTypes(
  mask:
    | {
        allowedLiabilityTypes?: readonly string[] | null;
        liabilityOnlyAllowedLiabilityTypes?: readonly string[] | null;
      }
    | null
    | undefined,
  isLiabilityOnly: boolean
): readonly string[] | undefined {
  if (isLiabilityOnly && mask?.liabilityOnlyAllowedLiabilityTypes?.length) {
    return mask.liabilityOnlyAllowedLiabilityTypes;
  }
  return mask?.allowedLiabilityTypes ?? undefined;
}

/** Whether any quotable standalone inland marine risk is present. (#33165) */
export function hasInlandMarineRisk(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  allowedRiskTypes?: readonly string[] | null
): boolean {
  return collectQuotableFarmRiskTypes(farmLocations, allowedRiskTypes).some((riskType) =>
    isInlandMarineRiskType(riskType)
  );
}

/**
 * Whether the given farm risks mix standalone inland marine with any other risk type. A standalone
 * inland marine risk insures scheduled personal property with no dwelling or structure on the
 * policy, so it can only be quoted on its own — several inland marine risks may share an
 * offer/policy, but never alongside a dwelling, renters, or FO-6 property risk.
 * (#33165 — docs/farm-standalone-inland-marine.md)
 */
export function hasMixedInlandMarineRiskTypes(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  allowedRiskTypes?: readonly string[] | null
): boolean {
  const riskTypes = collectQuotableFarmRiskTypes(farmLocations, allowedRiskTypes);
  return riskTypes.some((riskType) => isInlandMarineRiskType(riskType)) && !riskTypes.every(isInlandMarineRiskType);
}

/**
 * Whether any quotable risk can carry a farm discount factor. ClarionDoor applies the farm discount
 * factors on the dwelling, renters and FO-6 property nodes only: the liability node receives no discount
 * answers, and the standalone inland marine node receives them but prices as the sum of its scheduled
 * items (no factor applies). So an inland-marine-only farm and a liability-only farm (no risks at all)
 * have nothing for a discount to apply to. The discount cards, the documents and the CFM post-data
 * rules all key off this one predicate.
 *
 * Same truth table as `hasInsuredFarmProperty` by design (like `isEquipmentBreakdownEligibleRiskType`):
 * an allow-list, so an unknown type or the form-only `LiabilityOnly` sentinel is never discountable.
 * (#34855, #34854)
 */
export function hasDiscountableFarmRisk(
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined,
  allowedRiskTypes?: readonly string[] | null
): boolean {
  return hasInsuredFarmProperty(farmLocations, allowedRiskTypes);
}

/**
 * The only farm products that can carry standalone inland marine: full Farm (F) and the Farm + Auto
 * bundle (FA). Home (Z), Rental (Y) and everything else never write the risk type — they insure a
 * dwelling or a structure. Matched by option code, not by farm line — Z and Y also carry the F line.
 * (#33165)
 */
const INLAND_MARINE_POLICY_TYPES: readonly string[] = [policyTypes.Farm, policyTypes.FABundle];

/** Whether standalone inland marine may be quoted on the given farm product. (#33165) */
export function isInlandMarineAvailableForPolicyType(farmPolicyType: string | null | undefined): boolean {
  return !farmPolicyType || INLAND_MARINE_POLICY_TYPES.includes(farmPolicyType);
}

type FarmPolicyTypeSource = { selectedOption?: string | null; policyType?: string | null } | null | undefined;

/**
 * The farm line (F, Z or Y) of a quote (selectedOption) or a bound policy (policyType), first
 * source with a value wins. A Farm + Auto bundle (FA) is full farm. Throws instead of defaulting.
 */
export function getFarmPolicyType(...sources: FarmPolicyTypeSource[]): string {
  const type = sources.map((source) => source?.selectedOption || source?.policyType).find(Boolean);
  if (isFarmBasedPolicyType(type)) {
    return type as string;
  }
  if (type === policyTypes.FABundle) {
    return policyTypes.Farm;
  }
  throw new Error(`Not a farm policy type: '${type}'`);
}

/**
 * Enforces both standalone-inland-marine product rules, throwing on the first violation. Called at every
 * boundary that persists farm risks — offer creation, offer edit, endorsement, pre-bind — so a bad shape
 * can never be saved: the agent flow validates the same rules in staff-fe, and failing loudly here beats
 * silently dropping a risk the agent entered.
 *
 * 1. Inland marine is quotable only on Farm (F) and the Farm + Auto bundle (FA).
 * 2. On those products it can only be quoted on its own — never alongside a dwelling, renters or FO-6
 *    property risk. Several inland marine risks together are fine.
 *
 * `allowedRiskTypes` (the brand's quotable types) keeps a brand that cannot quote inland marine at all
 * from tripping either rule, since the brand gate drops those risks downstream. A caller that cannot
 * resolve the policy type passes none, and only rule 2 applies.
 * (#33165 — docs/farm-standalone-inland-marine.md)
 */
export function assertStandaloneInlandMarine({
  farmLocations,
  allowedRiskTypes,
  farmPolicyType
}: {
  farmLocations: readonly (DwellingRequirementLocation | null | undefined)[] | null | undefined;
  allowedRiskTypes?: readonly string[] | null;
  farmPolicyType?: string | null;
}): void {
  if (!hasInlandMarineRisk(farmLocations, allowedRiskTypes)) {
    return;
  }

  if (!isInlandMarineAvailableForPolicyType(farmPolicyType)) {
    throw new Error(
      `Inland marine is only available on Farm (F) and Farm + Auto (FA) policies: policy type ${farmPolicyType} cannot hold an inland marine risk.`
    );
  }

  if (hasMixedInlandMarineRiskTypes(farmLocations, allowedRiskTypes)) {
    throw new Error(
      'Inland marine can only be quoted on its own: a farm offer or policy cannot hold an inland marine risk alongside a dwelling, renters or other farm property risk. Multiple inland marine risks are allowed.'
    );
  }
}

/**
 * Flattens all risks from farm locations into a single array
 * @param farmLocations Array of farm locations
 * @returns Array of all risks from all locations, filtered for null/undefined values
 */
export function flattenFarmRisks(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFarmRiskDetail[] {
  if (!farmLocations || !Array.isArray(farmLocations)) {
    return [];
  }

  return farmLocations
    .filter((location): location is DynamoDbLocationDetail => location != null)
    .flatMap((location) => (location.risks?.filter(Boolean) || []) as DynamoDbFarmRiskDetail[]);
}

/**
 * Filters risks to only include dwelling risks (FO1, FO2, FO3)
 * @param risks Array of farm risks
 * @returns Array of dwelling risks only
 */
export function filterDwellingRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFODwellingRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFODwellingRisk =>
      !!risk &&
      !!risk.type &&
      (risk.type === FarmRiskType.Fo1 ||
        risk.type === FarmRiskType.Fo2 ||
        risk.type === FarmRiskType.Fo3 ||
        DWELLING_RISK_TYPES.includes(risk.type.toUpperCase()))
  );
}

/**
 * A year-built Coverage A override from brand config: the floor that applies to dwellings built in
 * `threshold` or earlier, plus the looser floor it replaced (if any).
 */
export type CoverageAYearBuiltOverrides = {
  threshold: number;
  minPercentAtOrBefore: number;
  grandfatheredMinPercentAtOrBefore?: number;
};

/**
 * The Coverage A floor, as a fraction of RCE, for a dwelling covered by a year-built override.
 *
 * #35312 raised CFM's Functional Replacement Cost floor (FO-2 RC, built 1950 or earlier) from 50%
 * to 80% of RCE. That floor only ever clamps upward, so applying the new value to a term already
 * written below it would silently re-price the dwelling. On an existing term the floor therefore
 * ratchets to the limit the term already carries: never raising it, never letting it be bought
 * down below it, and never dropping under the floor the term was written under.
 *
 * New business — and any config with nothing to grandfather — gets the tightened floor outright.
 *
 * Shared by the CFM farm post-data rules, the staff-fe Coverage A validation, and the staff-fe
 * help text so the three can never disagree about what an agent is allowed to enter.
 *
 * @param isExistingTerm whether `priorCoverageA` is a limit the policy is actually bound at
 * @param priorCoverageA Coverage A as the term already carries it
 * @param rce the dwelling's replacement cost estimate
 */
export function grandfatheredCoverageAMinPercent({
  yearBuiltOverrides,
  isExistingTerm,
  priorCoverageA,
  rce
}: {
  yearBuiltOverrides: CoverageAYearBuiltOverrides;
  isExistingTerm: boolean;
  priorCoverageA: number | null | undefined;
  rce: number | null | undefined;
}): number {
  const tightenedMinPercent = yearBuiltOverrides.minPercentAtOrBefore;
  const grandfatheredMinPercent = yearBuiltOverrides.grandfatheredMinPercentAtOrBefore;

  if (
    !isExistingTerm ||
    grandfatheredMinPercent === undefined ||
    grandfatheredMinPercent >= tightenedMinPercent ||
    !priorCoverageA ||
    !rce
  ) {
    return tightenedMinPercent;
  }

  return Math.max(grandfatheredMinPercent, Math.min(tightenedMinPercent, priorCoverageA / rce));
}

/**
 * Snapshots Coverage A for every dwelling risk on a policy, keyed by risk id.
 *
 * #35312: coverage floors that tighten after a term is written must not re-price it. The policy-mod
 * preview takes this snapshot of the STORED policy before applying an agent's requested change, and
 * hands it to the post-data rules as `priorDwellingCoverageA` so the floor can be grandfathered
 * against the limit the term actually carries. A dwelling with no numeric Coverage A is omitted
 * rather than recorded as 0 — a 0 would read as a grandfathered $0 limit and disable the floor.
 *
 * @param farmLocations Farm locations from the stored policy's merged segments
 * @returns Coverage A by dwelling risk id
 */
export function dwellingCoverageAByRiskId(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[] | null | undefined
): Record<string, number> {
  const coverageAByRiskId: Record<string, number> = {};
  for (const risk of filterDwellingRisks(flattenFarmRisks(farmLocations ?? []))) {
    const coverageA = risk.coverages?.coverageA;
    if (risk.id && typeof coverageA === 'number' && Number.isFinite(coverageA)) {
      coverageAByRiskId[risk.id] = coverageA;
    }
  }
  return coverageAByRiskId;
}

/** A dwelling's stored Coverage C and the inputs its Coverage C rule resolves from. #37093 */
export interface DwellingCoverageCInputs {
  type: string;
  coverageA: number | null;
  coverageC: number;
  residenceUsage: string | null;
  lossSettlement: string | null;
}

/**
 * Snapshots Coverage C, and the inputs its rule resolves from (risk type, Coverage A, residence usage,
 * loss settlement), for every dwelling risk on a policy, keyed by risk id. Handed to the post-data
 * rules as `priorDwellingCoverageCInputs`: on the term imported from GO, a Coverage C GO wrote below the
 * percent-of-Coverage-A minimum stays as it is only while every one of those inputs is unchanged
 * (#37093). A dwelling with no numeric Coverage C is omitted.
 *
 * @param farmLocations Farm locations from the stored policy's merged segments
 * @returns Coverage C and its rule inputs by dwelling risk id
 */
export function dwellingCoverageCInputsByRiskId(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[] | null | undefined
): Record<string, DwellingCoverageCInputs> {
  const inputsByRiskId: Record<string, DwellingCoverageCInputs> = {};
  for (const risk of filterDwellingRisks(flattenFarmRisks(farmLocations ?? []))) {
    const coverageC = risk.coverages?.coverageC;
    if (risk.id && typeof coverageC === 'number' && Number.isFinite(coverageC)) {
      inputsByRiskId[risk.id] = {
        type: risk.type,
        coverageA: risk.coverages?.coverageA ?? null,
        coverageC,
        residenceUsage: risk.building?.residenceUsage ?? null,
        lossSettlement: risk.coverages?.lossSettlement ?? null
      };
    }
  }
  return inputsByRiskId;
}

/**
 * Snapshots Coverage E for every FO-6 structure risk on a policy, keyed by risk id — the Coverage E
 * counterpart of dwellingCoverageAByRiskId, handed to the post-data rules as
 * `priorStructureCoverageE`. On a term imported from the legacy carrier, coverage floors are held to
 * the limit the term already carries rather than raising it (#36941). A structure with no numeric
 * Coverage E is omitted rather than recorded as 0.
 *
 * @param farmLocations Farm locations from the stored policy's merged segments
 * @returns Coverage E by structure risk id
 */
export function structureCoverageEByRiskId(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[] | null | undefined
): Record<string, number> {
  const coverageEByRiskId: Record<string, number> = {};
  for (const risk of filterPropertyStructureRisks(flattenFarmRisks(farmLocations ?? []))) {
    const coverageE = risk.coverages?.coverageE;
    if (risk.id && typeof coverageE === 'number' && Number.isFinite(coverageE)) {
      coverageEByRiskId[risk.id] = coverageE;
    }
  }
  return coverageEByRiskId;
}

/**
 * #37763: which risks arrived with a term, or why that is unknown. `unknown` is never "every risk arrived": a caller
 * that cannot tell must fail closed (devloai on #37825).
 */
export type RisksArrivedWithTerm =
  | { known: true; riskIds: string[] }
  | { known: false; reason: 'no-stored-segments' | 'segments-not-hydrated' };

/**
 * #37763: the ids of the risks that arrived with a term, in first-seen order: those on its first segment, plus those
 * on every later segment the migration import wrote (`importedAt`). The CFM GO import writes one segment per GO
 * version (#37380), so a structure GO added mid-term first appears on a later segment, and it came over from GO like
 * the rest (#37798). A segment an endorsement writes never carries `importedAt`, so a risk added on the platform is
 * not in the list. A renewal keeps every risk id, so its first segment lists what it carried from the expiring term.
 *
 * Unknown when there are no stored segments, or when the first segment's locations or risks are not hydrated (no
 * location array, an unresolved `_ref`, a risk without an id). A later segment is a delta and may leave locations
 * out, so only id-bearing risks on it count. staff-fe's carry-over marker (`carriedFromTermStart`) and the farm
 * post-data rules (`termArrivedRiskIds`) both read this, so they agree on which risks keep the limits they came over
 * with.
 *
 * @param segments The term's stored segments, oldest first
 * @returns The arrived risk ids, or why they are unknown
 */
export function riskIdsArrivedWithTerm(
  segments:
    | readonly (
        | { importedAt?: string | null; farmLocations?: (DynamoDbLocationDetail | null | undefined)[] | null }
        | null
        | undefined
      )[]
    | null
    | undefined
): RisksArrivedWithTerm {
  if (!segments?.length) {
    return { known: false, reason: 'no-stored-segments' };
  }
  const firstSegmentLocations = segments[0]?.farmLocations;
  if (
    !Array.isArray(firstSegmentLocations) ||
    firstSegmentLocations.some((location) => location != null && !Array.isArray(location.risks ?? [])) ||
    flattenFarmRisks(firstSegmentLocations).some((risk) => !risk?.id)
  ) {
    return { known: false, reason: 'segments-not-hydrated' };
  }
  const riskIds = new Set<string>();
  segments.forEach((segment, index) => {
    if (index !== 0 && !segment?.importedAt) {
      return;
    }
    for (const risk of flattenFarmRisks(Array.isArray(segment?.farmLocations) ? segment.farmLocations : [])) {
      if (risk?.id) {
        riskIds.add(risk.id);
      }
    }
  });
  return { known: true, riskIds: [...riskIds] };
}

/**
 * An FO-6 structure's roof loss settlement and everything the roof rules resolve it from: the
 * structure category, occupancy, building class, year built, structure loss settlement and roof
 * restriction. #37110
 */
export interface StructureRoofSettlementInputs {
  roofLossSettlement: string | null;
  structureLossSettlement: string | null;
  roofExclusionRestriction: string | null;
  buildingType: string | null;
  occupancy: string | null;
  buildingClass: number | null;
  yearBuilt: number | null;
}

// The form sends a cleared select as '' and GraphQL returns null, so both mean "unset".
const stringOrNull = (value: unknown): string | null =>
  value === '' || value === undefined || value === null ? null : String(value);

const numberOrNull = (value: unknown): number | null => {
  if (value === '' || value === undefined || value === null) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Snapshots one FO-6 structure's roof loss settlement and its rule inputs. #37110 */
export function structureRoofSettlementInputs(
  risk: { building?: unknown; coverages?: unknown } | null | undefined
): StructureRoofSettlementInputs {
  const building = (risk?.building ?? {}) as Record<string, unknown>;
  const coverages = (risk?.coverages ?? {}) as Record<string, unknown>;
  return {
    roofLossSettlement: stringOrNull(coverages.roofLossSettlement),
    structureLossSettlement: stringOrNull(coverages.structureLossSettlement),
    roofExclusionRestriction: stringOrNull(coverages.roofExclusionRestriction),
    buildingType: stringOrNull(building.buildingType),
    occupancy: stringOrNull(building.occupancy),
    buildingClass: numberOrNull(building.buildingClass),
    yearBuilt: numberOrNull(building.yearBuilt)
  };
}

/** True when two `structureRoofSettlementInputs` snapshots hold the same values. #37110 */
export function sameStructureRoofSettlementInputs(
  a: StructureRoofSettlementInputs | null | undefined,
  b: StructureRoofSettlementInputs | null | undefined
): boolean {
  if (!a || !b) {
    return false;
  }
  return (Object.keys(a) as (keyof StructureRoofSettlementInputs)[]).every((key) => a[key] === b[key]);
}

/**
 * Snapshots `structureRoofSettlementInputs` for every FO-6 structure on a policy, keyed by risk id.
 * Handed to the post-data rules as `priorStructureRoofSettlementInputs`: on a migrated lineage a
 * structure keeps the roof settlement it arrived with while none of these values has changed. #37110
 *
 * @param farmLocations Farm locations from the stored policy's merged segments
 * @returns The roof settlement and its rule inputs by structure risk id
 */
export function structureRoofSettlementInputsByRiskId(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[] | null | undefined
): Record<string, StructureRoofSettlementInputs> {
  const inputsByRiskId: Record<string, StructureRoofSettlementInputs> = {};
  for (const risk of filterPropertyStructureRisks(flattenFarmRisks(farmLocations ?? []))) {
    if (risk.id) {
      inputsByRiskId[risk.id] = structureRoofSettlementInputs(risk);
    }
  }
  return inputsByRiskId;
}

/**
 * An FO-2 / FO-3 dwelling's roof loss settlement and what the roof rules resolve it from: the risk type
 * and the dwelling's loss settlement. Follow-up to #37168
 */
export interface DwellingRoofSettlementInputs {
  type: string | null;
  roofLossSettlement: string | null;
  lossSettlement: string | null;
}

/** Snapshots one dwelling's roof loss settlement and its rule inputs. */
export function dwellingRoofSettlementInputs(
  risk: { type?: unknown; coverages?: unknown } | null | undefined
): DwellingRoofSettlementInputs {
  const coverages = (risk?.coverages ?? {}) as Record<string, unknown>;
  return {
    type: stringOrNull(risk?.type),
    roofLossSettlement: stringOrNull(coverages.roofLossSettlement),
    lossSettlement: stringOrNull(coverages.lossSettlement)
  };
}

/**
 * Snapshots `dwellingRoofSettlementInputs` for every dwelling on a policy, keyed by risk id. Handed to the
 * post-data rules as `priorDwellingRoofSettlementInputs`, so an agent's endorsement keeps a Replacement
 * Cost roof the stored policy already carries while none of these values has changed.
 *
 * @param farmLocations Farm locations from the stored policy's merged segments
 * @returns The roof settlement and its rule inputs by dwelling risk id
 */
export function dwellingRoofSettlementInputsByRiskId(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[] | null | undefined
): Record<string, DwellingRoofSettlementInputs> {
  const inputsByRiskId: Record<string, DwellingRoofSettlementInputs> = {};
  for (const risk of filterDwellingRisks(flattenFarmRisks(farmLocations ?? []))) {
    if (risk.id) {
      inputsByRiskId[risk.id] = dwellingRoofSettlementInputs(risk);
    }
  }
  return inputsByRiskId;
}

const REPLACEMENT_COST_SETTLEMENT = 'RC';

/**
 * Whether a user who cannot approve Replacement Cost keeps an RC roof on an FO-2 / FO-3 dwelling: only
 * when the stored policy already carries it (an underwriter approved it on an earlier change) and the
 * endorsement changed neither the roof settlement nor what it resolves from. A dwelling missing from
 * `prior` was added in this change, so its RC is the agent's own election. Follow-up to #37168
 *
 * @param prior The dwelling as stored (`priorDwellingRoofSettlementInputs[riskId]`)
 * @param arrived The dwelling as the endorsement sent it, before any rule changed it
 */
export function keepsStoredDwellingRoofReplacementCost(
  prior: DwellingRoofSettlementInputs | null | undefined,
  arrived: DwellingRoofSettlementInputs
): boolean {
  return (
    arrived.roofLossSettlement === REPLACEMENT_COST_SETTLEMENT &&
    !!prior &&
    (Object.keys(arrived) as (keyof DwellingRoofSettlementInputs)[]).every((key) => prior[key] === arrived[key])
  );
}

/**
 * Whether a user who cannot approve Replacement Cost keeps an RC roof on an FO-6 structure: only when the
 * stored policy already carries it and the endorsement changed neither the roof settlement nor any input
 * the roof rules resolve it from (`StructureRoofSettlementInputs`). Follow-up to #37168
 */
export function keepsStoredStructureRoofReplacementCost(
  prior: StructureRoofSettlementInputs | null | undefined,
  arrived: StructureRoofSettlementInputs
): boolean {
  return (
    arrived.roofLossSettlement === REPLACEMENT_COST_SETTLEMENT && sameStructureRoofSettlementInputs(prior, arrived)
  );
}

/**
 * Whether a user who cannot approve Replacement Cost keeps an RC structure loss settlement on an older
 * confinement or poultry building (#33612): only when the stored policy already carries it and the
 * endorsement changed neither it nor what the age rule resolves from (structure category, occupancy,
 * building class, year built). The roof is not one of those inputs: lowering the roof leaves the building's
 * approval standing. Follow-up to #37168
 */
export function keepsStoredStructureReplacementCost(
  prior: StructureRoofSettlementInputs | null | undefined,
  arrived: StructureRoofSettlementInputs
): boolean {
  return (
    arrived.structureLossSettlement === REPLACEMENT_COST_SETTLEMENT &&
    !!prior &&
    (['structureLossSettlement', 'buildingType', 'occupancy', 'buildingClass', 'yearBuilt'] as const).every(
      (key) => prior[key] === arrived[key]
    )
  );
}

/**
 * Filters risks to only include property structures risks (FO6)
 * @param risks Array of farm risks
 * @returns Array of property structures risks only
 */
export function filterPropertyStructureRisks(risks: DynamoDbFarmRiskDetail[]): DynamoDbFOPropertyStructuresRisk[] {
  return risks.filter(
    (risk): risk is DynamoDbFOPropertyStructuresRisk =>
      !!risk && !!risk.type && risk.type === FarmRiskType.Fo6Structures && 'coverages' in risk
  );
}

/**
 * Gets dwelling risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of dwelling risks only
 */
export function getDwellingRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFODwellingRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterDwellingRisks(allRisks);
}

/**
 * Gets property structures risks from farm locations in a single operation
 * @param farmLocations Array of farm locations
 * @returns Array of property structures risks only
 */
export function getPropertyStructureRisksFromLocations(
  farmLocations: (DynamoDbLocationDetail | null | undefined)[]
): DynamoDbFOPropertyStructuresRisk[] {
  const allRisks = flattenFarmRisks(farmLocations as DynamoDbLocationDetail[]);
  return filterPropertyStructureRisks(allRisks);
}

/**
 * Checks if a risk is a dwelling risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a dwelling type (FO1, FO2, or FO3)
 */
export function isDwellingRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo1 ||
    upperType === FarmRiskType.Fo2 ||
    upperType === FarmRiskType.Fo3 ||
    DWELLING_RISK_TYPES.includes(upperType)
  );
}

/**
 * Checks if a risk is a renters risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a renters type (FO4)
 */
export function isRentersRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return upperType === FarmRiskType.Fo4;
}

/**
 * Checks if a risk is a property risk type
 * @param riskType The risk type to check
 * @returns True if the risk is a property type (FO6)
 */
export function isPropertyRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }

  const upperType = riskType.toUpperCase();
  return (
    upperType === FarmRiskType.Fo6Structures.toUpperCase() ||
    upperType === FarmRiskType.Fo6Scheduled.toUpperCase() ||
    upperType === FarmRiskType.Fo6Unscheduled.toUpperCase()
  );
}

/**
 * Checks if a risk is a standalone inland marine risk (docs/farm-standalone-inland-marine.md)
 * @param riskType The risk type to check
 * @returns True if the risk is a standalone inland marine risk
 */
export function isInlandMarineRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }
  return riskType.toUpperCase() === FarmRiskType.InlandMarine.toUpperCase();
}

/**
 * Whether a risk carries at least one scheduled personal property item. SPP specifically: the other
 * scheduled coverage types (income, peak season, ...) are not inland marine items and cannot price a
 * standalone inland marine risk, whose SPP items are its entire premium.
 *
 * Shared by staff-fe's offer/new-offer Yup rules and recalc prechecks and by the CFM farm
 * underwriting rule, so the FE cannot call a risk empty that the BE calls populated — a risk with
 * only an INCOME coverage used to pass the BE rule and then fail at ClarionDoor with "did not
 * receive premium". (#33164 - docs/farm-standalone-inland-marine.md)
 */
export function hasInlandMarineSppItems(risk: unknown): boolean {
  // `unknown` rather than a risk shape: the callers span the DynamoDbFarmRiskDetail union (only some
  // members of which carry `coverages`) and staff-fe's plain Formik values.
  const { scheduleCoverages } = (risk as { coverages?: { scheduleCoverages?: unknown } } | null)?.coverages ?? {};
  if (!Array.isArray(scheduleCoverages)) {
    return false;
  }
  return scheduleCoverages.some(
    (coverage: { coverageType?: unknown; items?: unknown }) =>
      coverage?.coverageType === FarmScheduleCoverageType.SPP &&
      Array.isArray(coverage?.items) &&
      coverage.items.length > 0
  );
}

/**
 * Whether an FO-6 scheduled-property risk carries at least one Coverage F item ClarionDoor can
 * actually price. Those items ARE the risk's entire premium, so a risk without one rates to $0 —
 * CD answers `MsgStatus: Success` with a blank error text, and the all-or-nothing farm rate fails
 * with BLOCK 5042 ("Farm rating error") rather than returning a cheap quote.
 *
 * The positive-limit check, not a plain `length > 0`, is the point: a half-filled item still makes
 * the array non-empty while rating exactly as $0. Shared by the new-offer and offer Yup rules so
 * both agree on what "populated" means. (#34585, mirroring hasInlandMarineSppItems above.)
 */
export function hasRateableScheduledFarmPropertyItems(risk: unknown): boolean {
  // `unknown` for the same reason as hasInlandMarineSppItems: callers span the
  // DynamoDbFarmRiskDetail union and staff-fe's plain Formik values.
  const { coverageF } = (risk as { coverages?: { coverageF?: unknown } } | null)?.coverages ?? {};
  if (!Array.isArray(coverageF)) {
    return false;
  }
  return coverageF.some((item: { limit?: unknown } | null) => Number(item?.limit) > 0);
}

/**
 * Whether the risk type insures property in its own right — the dwelling, renters and property
 * families, deliberately excluding InlandMarine (which carries only scheduled items and is Farm-only,
 * see docs/farm-standalone-inland-marine.md).
 * @param riskType The risk type to check
 * @returns True if the risk type insures property
 */
export function isInsuredFarmPropertyRiskType(riskType: string | null | undefined): boolean {
  const type = riskType || '';
  return isDwellingRiskType(type) || isRentersRiskType(type) || isPropertyRiskType(type);
}

/**
 * #33410: canonical "can Equipment Breakdown be offered for this risk?" predicate. Equipment
 * breakdown rides on a node that insures property, so this is exactly the insured-property set.
 * Shared by the carrier farm seeds and the staff-fe policy-wide-endorsements gate so the two can
 * never drift.
 * @param riskType The risk type to check
 * @returns True if the risk type makes Equipment Breakdown eligible
 */
export function isEquipmentBreakdownEligibleRiskType(riskType: string | null | undefined): boolean {
  return isInsuredFarmPropertyRiskType(riskType);
}

/**
 * Checks if a risk is a dwelling (FO1/FO2/FO3) or a farm structure (FO6Structures).
 *
 * These are the only risk additions that should re-trigger underwriting questions on a
 * farm endorsement (issue #31439). Adding scheduled property (FO6Scheduled), unscheduled
 * property (FO6Unscheduled), or an FO4 renters dwelling does NOT require new UW answers.
 * @param riskType The risk type to check
 * @returns True if the risk is a dwelling or farm structure
 */
export function isDwellingOrStructureRiskType(riskType: string): boolean {
  if (!riskType) {
    return false;
  }
  return isDwellingRiskType(riskType) || riskType.toUpperCase() === FarmRiskType.Fo6Structures.toUpperCase();
}

/**
 * Farm risk "form families" — groups of risk types that share the same building + coverage shape in
 * the staff-fe risk detail forms. Switching a risk BETWEEN families invalidates the previously
 * entered building/coverage data (the field sets barely overlap), so that data must be reset;
 * otherwise incompatible fields (e.g. FO-6 agricultural buildingType/occupancy or coverage E) leak
 * into the dwelling rater and produce a "farm rating error". Switching WITHIN the dwelling family
 * (FO-1 ↔ FO-2 ↔ FO-3) preserves the data. (#32514 reopen)
 */
export const FarmRiskFamily = {
  Dwelling: 'dwelling', // FO-1 / FO-2 / FO-3 — dwelling building + A/B/C/D coverages
  Renters: 'renters', // FO-4 — contents-only renters
  Structures: 'structures', // FO-6 other structures — agricultural building + coverage E
  Scheduled: 'scheduled', // FO-6 scheduled farm property
  Unscheduled: 'unscheduled', // FO-6 unscheduled farm property
  InlandMarine: 'inlandMarine' // standalone inland marine
} as const;

export type FarmRiskFamilyValue = (typeof FarmRiskFamily)[keyof typeof FarmRiskFamily];

/**
 * Classifies a farm risk type into its form family, or null if the type is missing/unrecognized.
 * Case-insensitive. (#32514 reopen)
 */
export function getFarmRiskFamily(riskType: string | null | undefined): FarmRiskFamilyValue | null {
  if (!riskType) {
    return null;
  }
  if (isDwellingRiskType(riskType)) {
    return FarmRiskFamily.Dwelling;
  }
  if (isRentersRiskType(riskType)) {
    return FarmRiskFamily.Renters;
  }
  const upperType = riskType.toUpperCase();
  if (upperType === FarmRiskType.Fo6Structures.toUpperCase()) {
    return FarmRiskFamily.Structures;
  }
  if (upperType === FarmRiskType.Fo6Scheduled.toUpperCase()) {
    return FarmRiskFamily.Scheduled;
  }
  if (upperType === FarmRiskType.Fo6Unscheduled.toUpperCase()) {
    return FarmRiskFamily.Unscheduled;
  }
  if (upperType === FarmRiskType.InlandMarine.toUpperCase()) {
    return FarmRiskFamily.InlandMarine;
  }
  return null;
}

/**
 * Whether changing a farm risk's type from `fromType` to `toType` crosses a form-family boundary —
 * meaning the previously-entered building + coverage data no longer applies and must be reset to a
 * clean slate. Returns false when either type is missing/unrecognized (no safe reset decision) or
 * when both belong to the same family (e.g. FO-1 → FO-3, which share a building shape). (#32514 reopen)
 */
export function isCrossFamilyFarmRiskChange(
  fromType: string | null | undefined,
  toType: string | null | undefined
): boolean {
  const fromFamily = getFarmRiskFamily(fromType);
  const toFamily = getFarmRiskFamily(toType);
  if (!fromFamily || !toFamily) {
    return false;
  }
  return fromFamily !== toFamily;
}

export interface FarmAddressEntity {
  id: string;
  type: string;
  address: FarmAddressDetails;
}

/**
 * Extracts all unique addresses from farm locations
 * @param farmLocations Array of farm locations
 * @returns Array of address entities with id, type, and address, deduplicated by address
 */
export function getAllFarmAddressEntities(
  farmLocations?: (DynamoDbLocationDetail | null | undefined)[]
): FarmAddressEntity[] {
  const entities: FarmAddressEntity[] = [];

  if (!farmLocations || !Array.isArray(farmLocations)) {
    return entities;
  }

  farmLocations.forEach((location) => {
    if (!location) {
      return;
    }

    // Location address only - risks now use their parent location's address
    if (location.address) {
      entities.push({
        id: location.id,
        type: 'location',
        address: location.address
      });
    }
  });

  // Deduplicate by address (simple string comparison)
  const seen = new Set<string>();
  return entities.filter((entity) => {
    const addressKey = `${entity.address.address}_${entity.address.city}_${entity.address.state}_${entity.address.zip}`;
    if (seen.has(addressKey)) {
      return false;
    }
    seen.add(addressKey);
    return true;
  });
}

/**
 * Custom error for farm state validation failures
 */
export class FarmStateError extends Error {
  constructor(
    message: string,
    public readonly farmLocations?: any[]
  ) {
    super(message);
    this.name = 'FarmStateError';
  }
}

/**
 * Gets the primary state for farm operations - STRICT VERSION
 * Throws error if primary farm location doesn't have a state
 * For farm insurance, state MUST come from primary location only
 * @param farmLocations Array of farm locations
 * @returns Primary farm location state
 * @throws {FarmStateError} When farm location data is invalid or missing state
 */
export function getFarmPrimaryStateStrict(farmLocations?: any[]): string {
  if (!farmLocations || !Array.isArray(farmLocations) || farmLocations.length === 0) {
    throw new FarmStateError('No farm locations provided for state determination');
  }

  const primaryLocation = farmLocations[0];
  if (!primaryLocation?.address) {
    throw new FarmStateError('Primary farm location has no address', farmLocations);
  }

  const primaryState = primaryLocation.address.state;
  if (!primaryState || primaryState.trim() === '') {
    throw new FarmStateError('Primary farm location has no state', farmLocations);
  }

  return primaryState.trim();
}

/**
 * Determines if a quote/policy is a farm policy based on farmLocations presence
 * @param quote The quote or policy object
 * @returns True if the object has farmLocations with at least one location
 */
export function isFarmPolicy(quote: any): boolean {
  return quote?.farmLocations && Array.isArray(quote.farmLocations) && quote.farmLocations.length > 0;
}

/**
 * Normalizes county names by removing a " COUNTY" suffix, converting to uppercase, and trimming
 * surrounding whitespace. Used for consistent county name formatting across mine subsidence
 * defaults, restricted-county underwriting gates, and other county-based lookups — where a stray
 * leading/trailing space (manual staff entry, imported/mutual-carrier records, vendor lookups)
 * must not cause a silent match miss.
 * @param county The county name to normalize (e.g., "Bond County", "BOND COUNTY", " Cook ")
 * @returns Normalized county name in uppercase without suffix (e.g., "BOND"), or empty string if null/undefined
 */
export function normalizeCountyName(county: string | null | undefined): string {
  return (county?.replace(/ COUNTY/i, '').toUpperCase() ?? '').trim();
}

/**
 * Occupancy codes for Silos/Grain Bins (SG building type) that use Length × Width
 * dimensions instead of Diameter for sqFt calculation and Verisk 360Value submissions.
 * CC = Corn Crib, GS = Commodity Shed, GH = Grain/Feed Handling Center
 * GitHub Issue: #29355
 */
export const LENGTH_WIDTH_OCCUPANCIES: readonly string[] = ['CC', 'GS', 'GH'];

/**
 * Checks if an occupancy type uses length and width instead of diameter.
 * @param occupancy - The occupancy code
 * @returns True if occupancy uses length/width dimensions
 */
export function usesLengthWidth(occupancy: string | null | undefined): boolean {
  if (!occupancy) {
    return false;
  }
  return LENGTH_WIDTH_OCCUPANCIES.includes(occupancy);
}

// Moved to its own module so wind-hail-deductible-override.ts can use it without a cycle. #36996
export { resolveDeductibleAmount } from './deductible-amount';

// Re-export farm policy utilities
export { makePolicyDetailsFromSegments, getPrimaryFarmMortgage, isFullArrayDelta } from './farm-policy-utils';
export {
  calculateFarmSegmentDelta,
  dropEditsOfRemovedFarmItems,
  ensureFarmDeltaIdentifiers,
  ensureFarmLiabilityDeltaIdentifiers
} from './farm-segment-delta';
export {
  getArrayMergePolicy,
  applyFeAuthoritativeArrayFields,
  FULL_REPLACE_ARRAY_FIELD_NAMES,
  FE_AUTHORITATIVE_ARRAY_FIELD_NAMES,
  REGISTERED_ARRAY_FIELD_NAMES,
  KNOWN_WRONG_READ_FIELD_NAMES,
  PROVIDER_DATA_FIELDS
} from './segment-merge/array-merge-policy';
export type { ArrayMergePolicy, ArrayReadStrategy } from './segment-merge/array-merge-policy';
export {
  applyFeAuthoritativeScalarFields,
  clearsLegacyFo6RoofType,
  movesFo6RoofShapeOutOfRoofType,
  applyFeAuthoritativeLocationScalarFields,
  isRemovableRiskScalarField,
  REMOVABLE_RISK_BUILDING_FIELDS,
  REMOVABLE_RISK_COVERAGE_FIELDS,
  REMOVABLE_RISK_SCALAR_FIELD_NAMES,
  REMOVABLE_LOCATION_SCALAR_FIELD_NAMES
} from './segment-merge/removable-scalar-policy';
export {
  isWholeValueField,
  isRatingSnapshot,
  SEGMENT_RATING_SNAPSHOT_FIELD_NAMES,
  WHOLE_VALUE_FIELD_NAMES
} from './segment-merge/whole-value-policy';
export {
  FARM_DELTA_DIRECTIVES_FIELD,
  applyFarmDeltaDirectives,
  farmDeltaDirectives,
  type FarmDeltaDirectives,
  type FarmDeltaPath
} from './segment-merge/delta-directives';

// Re-export farm premium breakdown hierarchy
export { buildFarmPremiumBreakdownHierarchy, buildFarmSegmentPremiumBreakdown } from './farm-premium-breakdown';

// Re-export Coverage C default/rule helpers (GitHub issue #29084)
export {
  getDefaultFarmCoverageC,
  getFarmCoverageCMinimum,
  resolveFarmCoverageCRules,
  type FarmCoverageCRules,
  type FarmCoverageCBusinessRules
} from './coverage-c-defaults';

// Re-export Coverage D default/rule helpers (GitHub issue #30548)
export {
  getDefaultFarmCoverageD,
  isFarmCoverageDEditable,
  resolveFarmCoverageDRules,
  type FarmCoverageDRules,
  type FarmCoverageDBusinessRules
} from './coverage-d-defaults';

// Filed-form edition resolver for the Forreston Farm policy packet
export {
  FARM_FORM_EDITIONS,
  getEarliestEditionDate,
  resolveEditionOrEarliest,
  resolveFormEdition,
  type FarmFormEditionMap,
  type FormEdition
} from './farm-forms-editions';

// Signature-required filed forms + overlay coordinates (issue #30491)
export {
  SIGNATURE_REQUIRED_FARM_FORMS,
  FARM_FORM_SIGNATURE_COORDS,
  getFarmFormSignatureLayout,
  type SignatureCoord,
  type RestrictionTextCoord,
  type FarmFormSignatureLayout,
  type FarmFormSignatureCoords
} from './signature-required-forms';

// Application-packet notices — signed acknowledgments appended to the application
// packet that are NOT filed forms or endorsements (issue #30742).
export {
  FARM_APPLICATION_NOTICES,
  RECURRING_PAYMENT_AUTHORIZATION_NOTICE_BY_BRAND,
  type FarmApplicationNotice,
  type FarmApplicationNoticeLayout,
  type FarmApplicationNoticeId
} from './farm-application-notices';

// Farm interested-party description-label constants (issue #30591) —
// shared between staff-fe and docs-engine farm-data-mapper.
export {
  MANAGER_OR_LESSOR_RELATIONSHIPS,
  DESIGNATED_PREMISES_LABEL,
  getManagerOrLessorDescriptionLabel
} from './farm-interested-party-labels';

export { scrubFarmPartyAssociatedEntities } from './scrub-farm-party-entities';
export { reconcileBusinessNamedInsureds } from './reconcile-business-named-insureds';

// Issue #30825: shared empty-shape factory for farmLiabilityCoverage. Consumed by
// staff-fe (eager UX clearing) and the forreston post-data-rules (defensive backstop)
// so the two layers can't drift as fields are added. The default-builder repopulates
// Coverage L / M / aggregate when the user toggles Liability ON or switches
// Personal↔Commercial (#30825 follow-up).
export {
  getFarmLiabilityDefaults,
  buildDefaultFarmLiabilityCoverage,
  buildEmptyFarmLiabilityCoverage
} from './empty-farm-liability';

// #33939 — the ONE shared module that owns writing/reading a Loss History Detail entry:
// canonical entry shape, date normalization, source-specific builders, id logic, A+/offer merges,
// UW authz + provenance stamping, the rating projection, and the Loss Summary rollup. Consumed by
// the IMT importer, the A+ farm pull, the farm rater, the policy-mod + offer gates, and staff-fe.
export * from './loss-history';

export { FARM_UMUIM_NONE, snapFarmUmUimLimit, type SnappedFarmUmUimLimit } from './farm-umuim-limit';

// #36434: the farm payment-plan facts (frequency / method labels, View Schedule rows) shared by the
// staff-fe Price details panel and the farm Quote/Application mapper.
export {
  isFarmInstallmentPlan,
  farmPaymentFrequencyLabel,
  farmPaymentMethodLabel,
  farmSchedulePaymentAmount,
  getFarmPaymentScheduleRows,
  type FarmSchedulePayment,
  type FarmPaymentScheduleRow
} from './farm-payment-plan';

// #36383 item 9: the CFM Additional Named Insured counts the farm liability rater charges for, shared
// with the farm Dec/Quote/Application mapper's liability rows.
export {
  CFM_COMMERCIAL_ADDITIONAL_INSURED_RELATIONSHIP,
  countCfmAdditionalNamedInsureds,
  farmInterestedPartiesRatingChanged,
  priorCarrierAdditionalNamedInsuredBucket,
  type AdditionalNamedInsuredParty,
  type AdditionalNamedInsuredCounts
} from './farm-additional-named-insureds';

// #37124: the additional-insured forms the prior carrier listed for an imported party, recorded by the
// GO importer and printed by the farm dec mapper.
export {
  IMPORTED_ADDITIONAL_INSURED_FORMS,
  enforceImportedPartyForms,
  importedAdditionalInsuredForms,
  importedPlaceholderPartyLabel,
  importedPlaceholderPartyName,
  isImportedPlaceholderParty,
  type ImportedPlaceholderBucket,
  type ImportedPartyForms,
  type PartyWithImportedForms
} from './imported-party-forms';

// #36755: the CFM addtl_premises_rentYN flag counts (scheduled premises rented to others + every farm
// location after the first), shared with the farm Dec mapper's rented-locations row.
export {
  countCfmAdditionalFarmLocations,
  countCfmScheduledPremisesRented,
  isCfmScheduledPremisesRented,
  type PremisesRentedExtension
} from './cfm-additional-premises-rented';

// #36798/#36862: CFM Theft and Vandalism per dwelling (included / editable / surcharge), shared by the
// CFM farm rater, the staff dwelling checkbox and the farm docs mapper.
export { getCfmTheftVandalism, type CfmTheftVandalism, type CfmTheftVandalismInputs } from './cfm-theft-vandalism';

// #36536: the Coverage A charges GO's Exclusive option waives, offset by the CFM rater.
export { exclusiveWaivedFactor, type ExclusiveWaiver, type ExclusiveWaiverInputs } from './exclusive-waiver';

export {
  CLASS_1_STRUCTURE_MINIMUM_COVERAGE_E,
  COVERAGE_LIMIT_OVERRIDE_FLOOR,
  DEFAULT_MINIMUM_DWELLING_COVERAGE_A,
  STRUCTURE_MINIMUM_COVERAGE_E,
  minimumDwellingCoverageA,
  minimumStructureCoverageE
} from './coverage-limit-floors';

// #36996: the approved wind/hail deductible exception (GO's 1% on imported CFM structures / Coverage F).
export {
  GRANDFATHERED_WIND_HAIL_DEDUCTIBLE_FLOOR,
  type WindHailDeductibleHolder,
  isWindHailDeductibleOverridden,
  resolveWindHailDeductibleAmount
} from './wind-hail-deductible-override';

// #37093: approvals the CFM GO import records on migrated policies (RC without RCE, sub-base pool
// limit), the Coverage C a migrated renewal is raised to, and when it is grandfathered instead (#37109).
export {
  ABOVE_GROUND_POOL_INCLUDED_LIMIT,
  type PoolLimitApprovalHolder,
  type RcWithoutRceApprovalHolder,
  COVERAGE_C_ROUNDING_SHORTFALL_LIMIT,
  isGenuinelyBelowCoverageCMinimum,
  isPoolLimitApproved,
  isRcWithoutRceApproved,
  largestGrandfatheredCoverageC,
  wholeDollarCoverageCMinimum,
  triggersRcWithoutRceCheck
} from './migrated-limit-approvals';

// #37128: the CFM Home/Rental New Home factor (ClarionDoor zeroes `new_home_savings` there) and the
// term-year dwelling age the rater sends, shared by the CFM farm rater and the farm docs mapper.
export {
  CFM_HOME_RENTAL_NEW_HOME_FACTOR_BY_AGE,
  cfmHomeRentalNewHomeFactor,
  getDwellingHomeAge,
  receivesCfmHomeRentalNewHomeCredit
} from './cfm-new-home-credit';
export { getTermEffectiveYear } from './term-effective-year';

// #37141: the 360Value re-run rule for a migrated policy's dwellings (policy-mod trigger + staff-fe pop-up).
export { isMigrated360RerunChange, MIGRATED_DWELLING_360_RERUN_FIELDS } from './migrated-360-rerun';
